import { createSelector } from 'reselect';
import { CUSTOM_CALCULATION_DATA_FIELD_KEY_PREFIX } from '../../../../shared/constants/dataFieldKeys';
import { MAX_APP_CALCULATIONS, MAX_USER_CALCULATIONS } from '../../../../shared/constants/customCalculationConstants';
import { CUSTOM_CALCULATIONS } from '../../../../shared/constants/flipperFlags';
import { COLUMN_TYPE } from '../../common/support/utils';
import { FORMATTER } from '../../common/utils';
import { buildColumn } from '../../common/support/utils/buildColumn';
import { isFeatureEnabled } from '../../redux/domains/FlipperUpdates/Flipper.selectors';
import type { CustomCalculationReducer } from '../reducers/CustomCalculationReducer';
import type { CustomCalculationRecord } from '../types/customCalculations';
import type { IColumn } from '../../common/types/IColumn';
import type { RootState } from 'client/js/redux';

const EMPTY_ARRAY = [];

export const isCustomCalculationsEnabled = createSelector(
  (state: RootState) => isFeatureEnabled(state, CUSTOM_CALCULATIONS),
  (isEnabled) => isEnabled
);
const getCustomCalculationsState = (state: RootState): CustomCalculationReducer => state.customCalculations;
const getCalculationId = (state: RootState, calculationId?: string) => calculationId;

export const getCustomCalculationRecords: (state: RootState) => CustomCalculationRecord[] = createSelector(
  isCustomCalculationsEnabled,
  getCustomCalculationsState,
  (isEnabled, customCalculationsState) => {
    return isEnabled && customCalculationsState?.records ? Object.values(customCalculationsState.records) : EMPTY_ARRAY;
  }
);

export const getCustomCalculationColumns: (state: RootState) => IColumn[] = createSelector(
  getCustomCalculationRecords,
  (records) =>
    records.map(({ id, name, definition, isAppLevel }) => {
      const key = `${CUSTOM_CALCULATION_DATA_FIELD_KEY_PREFIX}${id}`;
      const type = COLUMN_TYPE.CUSTOM_CALCULATION;

      const equation = definition.equation;
      const formatter = FORMATTER[definition.format];
      const popover = {
        title: `${name}: Custom ${isAppLevel ? 'App' : 'User'} calculation`,
      };
      const display = name;

      return buildColumn({ type, key, overrides: { display, popover, formatter, id, equation, isAppLevel } });
    })
);

export const getCalculationRecord: (state: RootState, calculationId?: string) => undefined | CustomCalculationRecord =
  createSelector(
    isCustomCalculationsEnabled,
    getCustomCalculationsState,
    getCalculationId,
    (isEnabled, customCalculationsState, calculationId) => {
      const records = customCalculationsState?.records || {};
      if (!isEnabled || !calculationId || !records[calculationId]) {
        return;
      }
      return records[calculationId];
    }
  );

export const getUserCalculationRecords: (state: RootState) => CustomCalculationRecord[] = createSelector(
  getCustomCalculationRecords,
  (records) => records.filter((calculationRecord) => !calculationRecord.isAppLevel)
);

export const getAppCalculationRecords: (state: RootState) => CustomCalculationRecord[] = createSelector(
  getCustomCalculationRecords,
  (records) => records.filter((calculationRecord) => calculationRecord.isAppLevel)
);

export const getCanCreateUserCalculation: (state: RootState) => boolean = createSelector(
  getUserCalculationRecords,
  (userCalculationRecords) => userCalculationRecords.length < MAX_USER_CALCULATIONS
);

export const getCanCreateAppCalculation: (state: RootState) => boolean = createSelector(
  getAppCalculationRecords,
  (appCalculationRecords) => appCalculationRecords.length < MAX_APP_CALCULATIONS
);
